import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Checkbox, Divider, Modal, Typography } from 'antd';
import FileSaver from 'file-saver';
import { CloseXIcon } from '~/img';
import { Button } from '~/components';
import colors from '~/constants/colors';
import { useHistory } from 'react-router';
import paths from '~/pages/paths';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { useDirection } from '~/hooks/i18nHooks';
import { useGetApplication, useGetPayloadWithProvided } from '~/controllers/wizard';
import { KsaCompanyInformation } from '~/types/ksa/KsaCompanyInformation';
import { KsaAuthorizedSignatory } from '~/types/ksa/KsaAuthorizedSignatory';
import TermsAndConditionsModal from './TermsAndConditionsModal';
import FeesAndChargesDrawer from './FeesAndChargesDrawer';
import theme from '~/constants/theme';

interface Props {
  signatoryName?: string;
  contractBlob?: Blob;
  contractName?: string;
  onOpenSignatoryModal?: () => void;
  onSubmit: () => void;
  isSubmitLoading?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

const AgreementReviewSection: FC<Props> = ({
  signatoryName,
  contractBlob,
  contractName,
  onOpenSignatoryModal,
  onSubmit,
  isSubmitLoading,
  disabled,
  isLoading,
}) => {
  const { t } = useTranslation('ksaApplication');
  const history = useHistory();
  const isMobile = useIsMobile();
  const dir = useDirection();

  // Payload data for the signatory details modal
  const applicationQuery = useGetApplication();
  const payload = useGetPayloadWithProvided();
  const companyInfo = (payload?.provided?.companyInformation ??
    payload?.companyInformation) as KsaCompanyInformation | undefined;
  const signatory = payload?.authorizedSignatory as KsaAuthorizedSignatory | undefined;

  const [termsChecked, setTermsChecked] = useState(false);
  const [feesChecked, setFeesChecked] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [feesDrawerOpen, setFeesDrawerOpen] = useState(false);
  const [signatoryDetailsOpen, setSignatoryDetailsOpen] = useState(false);

  const canSubmit = termsChecked && feesChecked && !disabled;

  const handleTermsAgree = () => {
    setTermsChecked(true);
    setTermsModalOpen(false);
  };

  const handleFeesAgree = () => {
    setFeesChecked(true);
    setFeesDrawerOpen(false);
  };

  // Detail rows for the Authorized Signatory popup
  const signatoryRows = [
    { label: t('Merchant Name'), value: companyInfo?.commercialName ?? companyInfo?.legalName },
    { label: t('Authorized Name'), value: signatory?.fullName ?? signatoryName },
    { label: t('Commercial Registration No.'), value: companyInfo?.licenseNumber },
    { label: t('Unified Number'), value: companyInfo?.entityNumber },
  ].filter((row) => row.value);

  return (
    <>
      <Wrapper isMobile={isMobile} dir={dir}>
        {/* ── Authorized Signatory header ── */}
        {!isLoading && (
          <SignatoryHeader
            onClick={() => setSignatoryDetailsOpen(true)}
            isMobile={isMobile}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="SignatoryHeader-Icon"
            >
              <path
                d="M14.166 8.33366L15.8327 10.0003L19.166 6.66699M10.8327 5.00033C10.8327 6.84127 9.3403 8.33366 7.49935 8.33366C5.6584 8.33366 4.16602 6.84127 4.16602 5.00033C4.16602 3.15938 5.6584 1.66699 7.49935 1.66699C9.3403 1.66699 10.8327 3.15938 10.8327 5.00033ZM4.33268 18.3337H10.666C12.1388 18.3337 13.3327 17.1398 13.3327 15.667C13.3327 13.4579 11.5418 11.667 9.33268 11.667H5.66602C3.45688 11.667 1.66602 13.4579 1.66602 15.667C1.66602 17.1398 2.85993 18.3337 4.33268 18.3337Z"
                stroke="#0069B1"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Typography.Text className="SignatoryHeader-Text">
              {t('Authorized Signatory details')}
            </Typography.Text>
          </SignatoryHeader>
        )}

        {isLoading ? (
          <LoadingWrapper>
            <LoadingText>{t('Loading your agreement...')}</LoadingText>
            <Spinner />
          </LoadingWrapper>
        ) : (
          <>
            <AgreementCard isMobile={isMobile}>
              <CardBody checked={termsChecked}>
                <RedCheckbox
                  id="agreementReview_termsCheckbox"
                  checked={termsChecked}
                  onChange={() => undefined}
                  onClick={(e) => {
                    if (termsChecked) {
                      e.preventDefault();
                      setTermsChecked(false);
                    } else {
                      e.preventDefault();
                    }
                  }}
                />
                <Typography.Text className="CardBody-Text">
                  {t(
                    'I have read, understood and agreed to the Merchant Terms and Conditions and Privacy Notice'
                  )}
                </Typography.Text>
              </CardBody>
              <Divider className="Card-Divider" />
              <CardLink
                onClick={() => setTermsModalOpen(true)}
                id="agreementReview_reviewTermsLink"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="CardLink-Icon"
                >
                  <path
                    d="M11.6673 2.08317V3.99986C11.6673 4.93328 11.6673 5.39999 11.849 5.75651C12.0088 6.07012 12.2637 6.32508 12.5773 6.48487C12.9339 6.66653 13.4006 6.66653 14.334 6.66653H16.2507M7.50065 9.99984H12.5007M7.50065 13.3332H10.834M16.6673 8.59459V11.6665C16.6673 14.0001 16.6673 15.1668 16.2132 16.0581C15.8137 16.8421 15.1763 17.4796 14.3923 17.879C13.501 18.3332 12.3342 18.3332 10.0007 18.3332C7.6671 18.3332 6.50032 18.3332 5.60902 17.879C4.82502 17.4796 4.1876 16.8421 3.78812 16.0581C3.33398 15.1668 3.33398 14.0001 3.33398 11.6665V8.14832C3.33398 5.98898 3.33398 4.90931 3.72448 4.07203C4.13845 3.1844 4.85188 2.47097 5.73951 2.057C6.57679 1.6665 7.65646 1.6665 9.8158 1.6665C10.7594 1.6665 11.2312 1.6665 11.6767 1.7653C12.1512 1.87049 12.6035 2.05788 13.0134 2.31897C13.3983 2.56418 13.7319 2.89778 14.3991 3.56498L14.7147 3.88055C15.4353 4.60118 15.7956 4.9615 16.0533 5.38198C16.2818 5.75478 16.4501 6.16122 16.5522 6.58637C16.6673 7.0659 16.6673 7.57547 16.6673 8.59459Z"
                    stroke="#0069B1"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Typography.Text className="CardLink-Text">
                  {t('Review Terms and Conditions')}
                </Typography.Text>
              </CardLink>
            </AgreementCard>

            {/* ── Card 2 — Fees & Charges ── */}
            <AgreementCard isMobile={isMobile}>
              <CardBody checked={feesChecked}>
                <RedCheckbox
                  id="agreementReview_feesCheckbox"
                  checked={feesChecked}
                  onChange={() => undefined}
                  onClick={(e) => {
                    if (feesChecked) {
                      e.preventDefault();
                      setFeesChecked(false);
                    } else {
                      e.preventDefault();
                    }
                  }}
                />
                <Typography.Text className="CardBody-Text">
                  {t(
                    'I acknowledge that I have read the fees and charges and confirm that I have read and understood its contents prior to submitting this application.'
                  )}
                </Typography.Text>
              </CardBody>
              <Divider className="Card-Divider" />
              <CardLink
                id="agreementReview_viewFeesLink"
                onClick={() => setFeesDrawerOpen(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="CardLink-Icon"
                >
                  <path
                    d="M11.6673 2.08317V3.99986C11.6673 4.93328 11.6673 5.39999 11.849 5.75651C12.0088 6.07012 12.2637 6.32508 12.5773 6.48487C12.9339 6.66653 13.4006 6.66653 14.334 6.66653H16.2507M7.50065 9.99984H12.5007M7.50065 13.3332H10.834M16.6673 8.59459V11.6665C16.6673 14.0001 16.6673 15.1668 16.2132 16.0581C15.8137 16.8421 15.1763 17.4796 14.3923 17.879C13.501 18.3332 12.3342 18.3332 10.0007 18.3332C7.6671 18.3332 6.50032 18.3332 5.60902 17.879C4.82502 17.4796 4.1876 16.8421 3.78812 16.0581C3.33398 15.1668 3.33398 14.0001 3.33398 11.6665V8.14832C3.33398 5.98898 3.33398 4.90931 3.72448 4.07203C4.13845 3.1844 4.85188 2.47097 5.73951 2.057C6.57679 1.6665 7.65646 1.6665 9.8158 1.6665C10.7594 1.6665 11.2312 1.6665 11.6767 1.7653C12.1512 1.87049 12.6035 2.05788 13.0134 2.31897C13.3983 2.56418 13.7319 2.89778 14.3991 3.56498L14.7147 3.88055C15.4353 4.60118 15.7956 4.9615 16.0533 5.38198C16.2818 5.75478 16.4501 6.16122 16.5522 6.58637C16.6673 7.0659 16.6673 7.57547 16.6673 8.59459Z"
                    stroke="#0069B1"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Typography.Text className="CardLink-Text">
                  {t('View Fees and Charges')}
                </Typography.Text>
              </CardLink>
            </AgreementCard>
          </>
        )}

        {/* ── Actions ── */}
        <ActionsArea isMobile={isMobile}>
          <SubmitButton
            id="agreementReview_submitButton"
            type="primary"
            block={isMobile}
            disabled={isLoading || !canSubmit}
            loading={isSubmitLoading}
            onClick={onSubmit}
          >
            {t('Submit')}
          </SubmitButton>

        </ActionsArea>
      </Wrapper>

      {/* ── Authorized Signatory details popup ── */}
      <SignatoryModal
        open={signatoryDetailsOpen}
        onCancel={() => setSignatoryDetailsOpen(false)}
        footer={null}
        closable={false}
        width={isMobile ? '100vw' : 760}
        isMobile={isMobile}
        style={isMobile ? { bottom: 0, margin: 0, paddingBottom: 0, maxWidth: '100vw' } : undefined}
        title={
          <SignatoryTitleRow dir={dir}>
            <Typography.Text className="SignatoryModal-Title">
              {t('Authorized Signatory details')}
            </Typography.Text>
            <SignatoryCloseBtn
              onClick={() => setSignatoryDetailsOpen(false)}
              id="signatoryModal_closeBtn"
            >
              <CloseXIcon />
            </SignatoryCloseBtn>
          </SignatoryTitleRow>
        }
        centered
      >
        <ModalBody dir={dir} isMobile={isMobile}>
          {signatoryRows.map((row, idx) => (
            <React.Fragment key={row.label}>
              <DetailRow dir={dir}>
                <Typography.Text className="DetailRow-Label">{row.label}</Typography.Text>
                <Typography.Text className="DetailRow-Value">{row.value}</Typography.Text>
              </DetailRow>
              {/* {idx < signatoryRows.length - 1 && <RowDivider />} */}
            </React.Fragment>
          ))}
        </ModalBody>
      </SignatoryModal>

      {/* Terms & Conditions modal */}
      <TermsAndConditionsModal
        open={termsModalOpen}
        onAgree={handleTermsAgree}
        onClose={() => setTermsModalOpen(false)}
      />

      {/* Fees & Charges drawer */}
      <FeesAndChargesDrawer
        open={feesDrawerOpen}
        onAgree={handleFeesAgree}
        onClose={() => setFeesDrawerOpen(false)}
      />
    </>
  );
};

/* ─── Styled components ─────────────────────────────────────── */

interface ResponsiveProps {
  isMobile?: boolean;
  dir?: string;
}

const Wrapper = styled.div<ResponsiveProps>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: ${({ isMobile }) => (isMobile ? '0 0 24px' : '0 0 32px')};
  max-width: ${({ isMobile }) => (isMobile ? '100%' : '100%')};
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  direction: ${({ dir }) => dir ?? 'ltr'};
`;

const SignatoryHeader = styled.div<ResponsiveProps>`
  display: flex;
  align-items: center;
  gap: ${({ isMobile }) => (isMobile ? '8px' : '12px')};
  cursor: pointer;
  padding: ${({ isMobile }) => (isMobile ? '4px 0 0 17px' : '16px 20px')};
  margin-bottom: ${({ isMobile }) => (isMobile ? '0' : '0')};
  transition: opacity 0.15s;
  background-color: ${({ isMobile }) => (isMobile ? 'transparent' : '#F4F8FC')};
  border-radius: ${({ isMobile }) => (isMobile ? '0' : '12px')};

  &:hover {
    opacity: 0.75;
  }

  .SignatoryHeader-Icon {
    color: ${colors.primaryColor};
    font-size: ${({ isMobile }) => (isMobile ? '18px' : '20px')};
    display: flex;
    align-items: center;
  }

  .SignatoryHeader-Text {
    color: ${colors.primaryColor};
    font-family: inherit;
    font-size: ${({ isMobile }) => (isMobile ? '14px' : '14px')};
    font-weight: 500;
    line-height: 22px;
  }
`;

const AgreementCard = styled.div<ResponsiveProps>`
  background: ${({ isMobile }) => (isMobile ? '#ffffff' : '#f4f8fc')};
  border-radius: 12px;
  box-shadow: ${({ isMobile }) =>
    isMobile
      ? '0px 1px 4px 0px rgba(0, 89, 150, 0.08)'
      : 'none'};
  padding: 16px;
  overflow: hidden;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;

  .Card-Divider {
    display: block;
    margin: 0;
    width: 100%;
    border-top: ${({ isMobile }) => (isMobile ? '1px solid #f0f4f8' : '1px solid #E2E8F0')};
  }
`;

const CardBody = styled.div<ResponsiveProps & { checked?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ isMobile }) => (isMobile ? '12px' : '16px')};

  .ant-checkbox-wrapper {
    // margin-top: 2px;
    flex-shrink: 0;
  }

  .CardBody-Text {
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    line-height: 160%;
    color: ${({ checked }) =>
    checked
      ? 'var(--Neutral-Color-Text-colorText, #070707)'
      : 'var(--Neutral-Color-Text-colorTextTertiary, #8F8F8F)'};
  }
`;

/**
 * Red checkbox — overrides Ant Design's default blue with brand red (#E64360 = colors.red[1100])
 */
const RedCheckbox = styled(Checkbox)`
  /* Unchecked border */
  .ant-checkbox-inner {
    border-radius: 4px;
    border-color: #d9d9d9;
    width: 18px;
    height: 18px;
  }

  /* Checked: red background + red border */
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${colors.red[1100]} !important;
    border-color: ${colors.red[1100]} !important;
  }

  /* Hover state */
  &:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: ${colors.red[1100]} !important;
  }

  /* Focus ring */
  .ant-checkbox-checked::after {
    border-color: ${colors.red[1100]} !important;
  }

  /* Indeterminate */
  .ant-checkbox-indeterminate .ant-checkbox-inner::after {
    background-color: ${colors.red[1100]} !important;
  }
`;

const CardLink = styled.div<ResponsiveProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.75;
  }

  .CardLink-Icon {
    color: ${colors.primaryColor};
    font-size: 18px;
    flex-shrink: 0;
  }

  .CardLink-Text {
    color: ${colors.primaryColor};
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }
`;

const ActionsArea = styled.div<ResponsiveProps>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: ${({ isMobile }) => (isMobile ? '40px' : '16px')};
  width: 100%;

  ${({ isMobile }) =>
    !isMobile &&
    css`
      flex-direction: row;
      align-items: center;
    `}
`;

const SubmitButton = styled(Button) <ResponsiveProps>`
  height: 52px;
  border-radius: ${({ isMobile }) => (isMobile ? '10px' : '12px')};
  font-size: 15px;
  font-weight: 600;
  width: 100%;
  min-width: 100%;

  &:disabled {
    background: ${({ isMobile }) => (isMobile ? '#e8eaed' : '#F4F8FC')} !important;
    border: ${({ isMobile }) => (isMobile ? '1px solid #e8eaed' : '1px solid #E2E8F0')} !important;
    color: #9ca3af !important;
  }
`;



/* ── Signatory Details Modal ── */

const SignatoryModal = styled(Modal) <{ dir?: string; isMobile?: boolean }>`
  .ant-modal-header {
    padding: ${({ isMobile }) => (isMobile ? '26px 24px 16px 24px' : '40px 40px 30px 40px')};
    border-bottom: none;
    border-radius:${({ isMobile }) => (isMobile ? '20px 20px 0 0' : '16px')} !important;
  }    
  .ant-modal-body {
    padding: 0;
  }
  .SignatoryModal-Title {
    font-family: Inter, sans-serif;
    font-size: ${({ isMobile }) => (isMobile ? '24px' : '20px')};
    font-style: normal;
    font-weight: 500;    
    color: #1a1a2e;
  }
    @media (max-width: ${theme.breakpoints.sm}px) {
        position: fixed !important;
        bottom: 0 !important;
        top: auto !important;
        left: 0 !important;
        right: 0 !important;
        margin: 0 !important;
        width: 100vw !important;
        max-width: 100vw !important;
        padding-bottom: 0 !important;
    
        .ant-modal-content {
          border-radius: 20px 20px 0 0;
        }
      }
`;

const SignatoryTitleRow = styled.div<{ dir?: string }>`
  display: flex;  
  align-items: center;
  justify-content: space-between;

  .SignatoryModal-Title {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif !important;
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  }
`;

const SignatoryCloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  // &:hover {
  //   background: #f5f5f5;
  // }
`;

const ModalBody = styled.div<{ dir?: string, isMobile?: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '16px 24px 32px 24px' : '0px 40px 30px 40px')};  
  direction: ${({ dir }) => dir ?? 'ltr'};
`;

const DetailRow = styled.div<{ dir?: string }>`
  display: flex;  
  align-items: center;
  justify-content: space-between;
  // gap: 16px;
  padding: 9px 0;

  .DetailRow-Label {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #4A4A4A;
    flex-shrink: 0;
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  }

  .DetailRow-Value {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #1a1a2e;
    text-align: ${({ dir }) => (dir === 'rtl' ? 'left' : 'right')};
    word-break: break-word;
  }
`;

const RowDivider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 0;
`;

const Spinner = styled.div`
  border: 4px solid #E2E8F0;
  border-top-color: transparent;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 48px 24px;
  background: #f4f8fc;
  border-radius: 12px;
  min-height: 240px;
  width: 100%;
`;

const LoadingText = styled.p`
  margin: 0;
  font-family: Inter, Tajawal, sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #1a1a2e;
`;

export default AgreementReviewSection;
